/**
 * RAWGAPIClient module for interfacing with RAWG's API
 * ES Module version for web UI - uses backend proxy to avoid CORS
 */

import axios from "axios";

const PLACEHOLDER_IMAGE = "https://placehold.co/460x215/1a1a2e/67e8f9?text=No+Image";

const BACKEND_PROXY = (
  import.meta.env.VITE_PROXY_URL || "http://localhost:4005"
).replace(/\/+$/, "");

/**
 * Picks the best available image from a RAWG item, trying multiple sources
 */
function pickImage(data) {
  if (data.background_image) return data.background_image;
  if (data.background_image_additional) return data.background_image_additional;
  const screenshot = data.short_screenshots?.find((s) => s.image) || data.screenshots?.find((s) => s.image);
  if (screenshot) return screenshot.image;
  return PLACEHOLDER_IMAGE;
}

/**
 * Parses a RAWG search result item into a GameData object
 * Note: search results don't include developers, so creator is empty
 */
function parseSearchResult(item) {
  return {
    steamID: `rawg-${item.id}`,
    source: "rawg",
    title: item.name || "",
    imageURL: pickImage(item),
    genres: (item.genres || []).map((g) => g.name),
    storeURL: `https://rawg.io/games/${item.slug}`,
    creator: "",
    releaseDate: item.released ? new Date(item.released) : new Date(),
    isDLC: false,
  };
}

/**
 * Parses a RAWG detail response into a GameData object
 */
function parseDetailResponse(data) {
  return {
    steamID: `rawg-${data.id}`,
    source: "rawg",
    title: data.name || "",
    imageURL: pickImage(data),
    genres: (data.genres || []).map((g) => g.name),
    storeURL: `https://rawg.io/games/${data.slug}`,
    creator: data.developers?.[0]?.name || "",
    releaseDate: data.released ? new Date(data.released) : new Date(),
    isDLC: false,
  };
}

/**
 * Searches for games by query string using RAWG API
 * @param {string} query - Search query string
 * @returns {Promise<Object>} - { success, data: { games[], totalCount } } or { success: false, error }
 */
export async function searchGamesByQuery(query) {
  try {
    if (!query || typeof query !== "string" || query.trim().length === 0) {
      return { success: false, error: "Search query cannot be empty" };
    }

    const response = await axios.get(`${BACKEND_PROXY}/api/rawg/search`, {
      params: { query },
      timeout: 15000,
    });

    if (!response.data || !response.data.results) {
      return { success: false, error: "Failed to retrieve search results from RAWG" };
    }

    const games = response.data.results
      .filter((item) => item.name && item.id)
      .slice(0, 10)
      .map(parseSearchResult);

    return {
      success: true,
      data: { games, totalCount: games.length },
    };
  } catch (error) {
    if (error.code === "ECONNABORTED") {
      return { success: false, error: "Request timeout - RAWG API is taking too long to respond" };
    }
    if (error.response?.status === 503) {
      return { success: false, error: "RAWG API is not configured on the server" };
    }
    if (error.response) {
      return { success: false, error: `RAWG API error: ${error.response.status} ${error.response.statusText}` };
    }
    if (error.request) {
      return { success: false, error: "Backend proxy is unavailable" };
    }
    return { success: false, error: `Failed to search RAWG: ${error.message}` };
  }
}

/**
 * Retrieves game details by RAWG ID
 * @param {string} rawgId - The RAWG numeric ID (without prefix)
 * @returns {Promise<Object>} - { success, data: gameData } or { success: false, error }
 */
export async function getGameByID(rawgId) {
  try {
    const response = await axios.get(`${BACKEND_PROXY}/api/rawg/games/${rawgId}`, {
      timeout: 15000,
    });

    if (!response.data || !response.data.id) {
      return { success: false, error: "Game not found" };
    }

    return {
      success: true,
      data: parseDetailResponse(response.data),
    };
  } catch (error) {
    if (error.code === "ECONNABORTED") {
      return { success: false, error: "Request timeout - RAWG API is taking too long to respond" };
    }
    if (error.response) {
      return { success: false, error: `RAWG API error: ${error.response.status} ${error.response.statusText}` };
    }
    if (error.request) {
      return { success: false, error: "Backend proxy is unavailable" };
    }
    return { success: false, error: `Failed to retrieve game data: ${error.message}` };
  }
}
